import { Button } from '@components/Button.tsx';
import { ButtonType } from '@common/types/button';
import { type CustomDirectusTypes } from '@funnellab/shared/src/types/directus.ts';
import { IconType } from '@src/IconType.ts';
import { ImportButton } from '@src/components/jobWizard/import/ImportButton';
import { JobCreatedModal } from '@components/modals/JobCreatedModal.tsx';
import { type JobStatus } from '@funnellab/shared/src/types/directus-enums.ts';
import { JobStatusBadge } from '@components/JobStatusBadge.tsx';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { NO_PAGINATION_PAGE_SIZE } from '@src/config/constants';
import { RowActions } from '@src/components/jobOverview/RowActions';
import { SearchParamKeys } from '@funnellab/shared/src/types/SearchParamKeys.ts';
import { directusClient } from '@src/api/DirectusClient.ts';
import { getJobDetailsUrl, getNewJobWizardUrl } from '@src/config/paths';
import { jobTableQuery } from '@src/api/queries/job.ts';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DirectusTable, {
  type DirectusTableHandle,
} from '@components/directusTable/DirectusTable.tsx';
import dayjs, {
  DEFAULT_GERMAN_DATE_FORMAT,
} from '@funnellab/shared/src/utils/dayjs';
import { DefaultView } from '@src/components/base/DefaultView';

export default function JobsView() {
  const tableRef = useRef<DirectusTableHandle>(undefined);

  const navigate = useNavigate();
  const { t } = useTranslation('view', { keyPrefix: 'jobs' });
  const [params, setParams] = useSearchParams();
  const [successModalOpen, setSuccessModalOpen] = useState(
    params.has(SearchParamKeys.SHOW_SUCCESS),
  );

  const query = useMemo(() => jobTableQuery, []);

  const toggleSuccessModal = useCallback(
    (open: boolean) => {
      if (!open) {
        setParams({});
        setSuccessModalOpen(false);
      }
    },
    [setParams],
  );

  const reload = useCallback(() => {
    tableRef.current?.reload(true);
  }, []);

  const actionButtons = useMemo(
    () => (
      <>
        <ImportButton />
        <Link className="inline-flex" to={getNewJobWizardUrl()}>
          <Button
            label={t('addJob')}
            buttonType={ButtonType.PRIMARY}
            IconLeading={IconType.Add}
            testId={'create-job'}
            className="w-full"
          />
        </Link>
      </>
    ),
    [t],
  );

  return (
    <DefaultView header={t('title')} actionButtons={actionButtons}>
      <DirectusTable<CustomDirectusTypes, 'job', typeof query>
        ref={tableRef}
        itemsPerPage={NO_PAGINATION_PAGE_SIZE}
        collection="job"
        directusClient={directusClient._client}
        query={query}
        getId={(rowData) => rowData.id}
        initialSort={{ fieldName: 'date_updated', direction: 'DESC' }}
        embedded
        selectedColumns={[
          'idExternal',
          'title',
          'status',
          'organization.name',
          'date_updated',
          'actions',
        ]}
        columnOverrides={{
          'idExternal': {
            sortable: true,
            renderCell: ({ rowData }) => <div>{rowData.idExternal ?? '-'}</div>,
            headContent: t('columnId'),
            cellClassName: '!hidden sm:!table-cell',
          },
          'title': {
            sortable: true,
            renderCell: ({ rowData }) => <div>{rowData.title ?? ''}</div>,
            headContent: t('columnTitle'),
            cellClassName: '!font-medium sm:!font-normal !pr-25 sm:!pr-3',
          },
          'status': {
            sortable: true,
            renderCell: ({ rowData }) => (
              <JobStatusBadge
                state={rowData.status as JobStatus}
                nextRenewal={rowData.nextRenewal}
              />
            ),
            headContent: t('columnStatus'),
            cellClassName: '!absolute right-3 top-6 sm:!static sm:!table-cell',
          },
          'organization.name': {
            sortable: true,
            headContent: t('columnOrganization'),
            cellClassName:
              '!block sm:!table-cell !pt-0 sm:!pt-4 !pr-25 sm:!pr-3',
            renderCell: ({ rowData }) => (
              <div>{rowData.organization?.name ?? '-'}</div>
            ),
          },
          'date_updated': {
            sortable: true,
            headContent: t('columnDateUpdated'),
            cellClassName: '!hidden sm:!table-cell',
            renderCell: ({ rowData }) => {
              if (!rowData.date_updated) {
                return <span>-</span>;
              }
              return (
                <span>
                  {dayjs(rowData.date_updated).format(
                    DEFAULT_GERMAN_DATE_FORMAT,
                  )}
                </span>
              );
            },
          },
          'actions': {
            headContent: '',
            sortable: false,
            cellClassName: '!hidden sm:!table-cell sm:min-w-28',
            renderCell: ({ rowData }) => (
              <RowActions rowData={rowData} reload={reload} />
            ),
          },
        }}
        onRowClick={(rowData, event) => {
          if (event.defaultPrevented) {
            // This happens when a button in a cell is clicked. In that case, the row should not be clicked.
            return;
          }

          // Note: when status is in draft the user will be redirected to the wizard via the jobsDetailsViewLoader
          navigate(getJobDetailsUrl(rowData.id));
        }}
        getRowLink={getJobDetailsUrl}
      />
      <JobCreatedModal open={successModalOpen} setOpen={toggleSuccessModal} />
    </DefaultView>
  );
}
